'use client'
import React, { ReactElement, ReactNode, useContext, useEffect, useRef } from 'react'
import { FilterDropdown } from '@/components/ui/wizard/filter/FilterModal'
import { PriceDropDown } from '@/components/ui/wizard/filter/Price'
import { MIN_NUMBER_OF_GUESTS, useWizardSelector } from '@/components/ui/wizard/WizardState'
import { GuestDropDown } from '@/components/ui/wizard/filter/Guests'
import { RatingDropDown } from '@/components/ui/wizard/filter/Rating'
import { LocationDropdown } from '@/components/ui/wizard/filter/Location'
import { ConfigContext } from '@/components/common/ConfigContext'
import { OrderDropdown, SORT_CRITERIA } from '@/components/ui/wizard/filter/Order'
import { Currency } from '@/types/Page'
import classNames from 'classnames'
import _sum from 'lodash/sum'
import { FilterModalType } from '@/lib/sharedConstants'
import { useOnMobileOrTablet } from '@/lib/hooks'
import { TIME_SEARCH_PARAMETERS_UPDATED } from '@/components/ui/wizard/constants'
import { SearchPageContext } from '@/components/pages/search-page/SearchPageContext'
import { setActiveStep } from '@/components/ui/wizard/WizardActions'
import MapIcon from '../../../../icons/filter-map-mobile.svg'
import { FORM_ELEMENT_STYLE } from '@/components/elements/common-styles'
import { SelectIcon } from '@/components/common/InputIcons'
import { useTranslations } from 'next-intl'
import FilterIcon from '@/icons/filter.svg'
import { cx } from '@/lib/utils/utils'

interface SearchOptionsButtonProps {
  onClick: () => void
  label: ReactNode
  id: string
}

function SearchOption(props: SearchOptionsButtonProps): ReactElement {
  return (
    <>
      <SearchOptionButton
        onClick={() => {
          props.onClick()
        }}
        additionalClassName="relative px-3 capitalize pr-10"
        id={props.id}
      >
        {props.label}
        <SelectIcon />
      </SearchOptionButton>
    </>
  )
}

interface SearchOptionsProps {
  closeWizard: () => void
  currency: Currency | undefined
}

function DotIndicator(props: { additionalClassName: string }): ReactElement {
  return (
    <div
      className={classNames(
        'absolute rounded-[100%] bg-white lg:bg-primary',
        props.additionalClassName,
      )}
    ></div>
  )
}

export function MobileMapButton(props: {
  onClick: () => void
  additionalClassName?: string
  iconSize?: string
}): ReactElement {
  const t = useTranslations('MobileMenu')

  return (
    <button
      onClick={props.onClick}
      className={classNames(
        'ml-4 flex cursor-pointer items-center rounded-md border-gray-300 text-stone-600 lg:before:bg-center',
        props.additionalClassName,
      )}
    >
      <MapIcon
        className={classNames(
          'mr-1.5 stroke-stone-600 stroke-2',
          props.iconSize || 'h-[18px] w-[21px]',
        )}
      />
      {t('map')}
    </button>
  )
}

function DropDowns(props: { currency: Currency | undefined }): ReactElement {
  const { searchOptionsType } = useContext(SearchPageContext)
  const onMobile = useOnMobileOrTablet()

  if (onMobile) {
    return <></>
  }

  return (
    <>
      {searchOptionsType === FilterModalType.LOCATION && <LocationDropdown />}
      {searchOptionsType === FilterModalType.GUESTS && <GuestDropDown />}
      {searchOptionsType === FilterModalType.ORDER && <OrderDropdown />}
      {searchOptionsType === FilterModalType.RATING && <RatingDropDown />}
      {searchOptionsType === FilterModalType.PRICE && (
        <PriceDropDown currency={props.currency as Currency} />
      )}
      {searchOptionsType === FilterModalType.ALL && <FilterDropdown currency={props.currency} />}
    </>
  )
}

function SearchOptionButton(props: {
  onClick: (event) => void
  additionalClassName: string
  children: React.ReactNode
  id: string
}) {
  return (
    <button
      id={`filter-button-${props.id}`}
      className={classNames(
        FORM_ELEMENT_STYLE,
        'mr-2 flex items-center',
        props.additionalClassName,
      )}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  )
}

export default function SearchOptions(props: SearchOptionsProps): ReactElement {
  const numberOfGuestInStore = useWizardSelector((state) => state.numberOfGuests)
  const ratingInStore = useWizardSelector((state) => state.rating)
  const starsInStore = useWizardSelector((state) => state.stars)
  const locationFilterInStore = useWizardSelector((state) => state.locationFilter)
  const maximumPriceInStore = useWizardSelector((state) => state.maximumPrice)
  const order = useWizardSelector((state) => state.order)
  const config = useContext(ConfigContext)!
  const flexibleButtonAreaRef = useRef<HTMLDivElement>(null)
  const staticButtonAreaRef = useRef<HTMLDivElement>(null)
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const { setSearchOptionsType, setDisplayMap } = useContext(SearchPageContext)
  const t = useTranslations()
  const onMobile = useOnMobileOrTablet()

  useEffect(() => {
    window.addEventListener('resize', () => {
      recalculateFilterButtonAreaSize()
    })
    window.addEventListener(TIME_SEARCH_PARAMETERS_UPDATED, () =>
      setTimeout(recalculateFilterButtonAreaSize),
    )
  }, [])

  function getButtonWidths(container: HTMLDivElement): number[] {
    return ([].slice.call(container.children) as HTMLButtonElement[]).map(
      (button: HTMLButtonElement) => button.offsetWidth + 8,
    )
  }

  function recalculateFilterButtonAreaSize(): void {
    if (
      !(flexibleButtonAreaRef.current && staticButtonAreaRef.current && filterContainerRef.current)
    ) {
      return
    }

    const flexibleAreaButtonWidths = getButtonWidths(flexibleButtonAreaRef.current)
    const staticAreaButtonWidths = getButtonWidths(staticButtonAreaRef.current)
    const maximumSpaceRequiredFlexibleArea = _sum(flexibleAreaButtonWidths)
    const maximumSpaceRequiredStaticArea = _sum(staticAreaButtonWidths)

    const totalAvailableSpace = filterContainerRef.current.offsetWidth
    const maximumSpaceRequired = maximumSpaceRequiredStaticArea + maximumSpaceRequiredFlexibleArea

    if (maximumSpaceRequired < totalAvailableSpace) {
      flexibleButtonAreaRef.current.style.width = ''
    }

    const availableSpace = totalAvailableSpace - maximumSpaceRequiredStaticArea

    let flexibleWidth = 0

    for (const width of flexibleAreaButtonWidths) {
      const nextSize = flexibleWidth + width - 1
      if (nextSize > availableSpace) {
        flexibleButtonAreaRef.current.style.width = `${flexibleWidth}px`
        return
      } else {
        flexibleWidth = nextSize
      }
    }
  }

  function numberLocationFilters(): number {
    return locationFilterInStore.filter((l) => config.searchFilters.location.includes(l)).length
  }

  function locationLabel(translate: (s: string) => string): ReactNode {
    if (numberLocationFilters() === 0) {
      return t('SearchPage.buttonLocation')
    }

    return `${translate(locationFilterInStore[0].value)} ${
      numberLocationFilters() > 1 ? `+${numberLocationFilters() - 1}` : ''
    }`
  }

  function orderLabel(): ReactNode {
    return t(
      (order !== undefined && `SearchPage.SortCriteria.${SORT_CRITERIA[order]}`) ||
        'SearchPage.buttonAllSorting',
    )
  }

  function filterActive(): boolean {
    return !!(
      numberOfGuestInStore !== MIN_NUMBER_OF_GUESTS ||
      ratingInStore ||
      starsInStore ||
      locationFilterInStore.length !== 0 ||
      maximumPriceInStore
    )
  }
  return (
    <>
      <div
        className={classNames(
          'relative flex min-h-[48px] items-center justify-between text-lg text-stone-600 lg:hidden',
        )}
      >
        <MobileMapButton onClick={() => setDisplayMap(true)} additionalClassName="h-full" />
        <div
          className={classNames('flex items-center gap-x-2', {
            'fixed bottom-5 left-1/2 -translate-x-1/2 justify-center whitespace-nowrap rounded-md bg-primary px-6 py-3 font-semibold text-primary-complement':
              onMobile,
            'relative h-full': !onMobile,
          })}
          onClick={(e) => {
            setSearchOptionsType(FilterModalType.ALL)
            e.stopPropagation()
          }}
        >
          <div
            className={cx({
              "relative after:absolute after:right-[-5px] after:top-[-4px] after:block after:h-[6px] after:w-[6px] after:rounded-full after:bg-primary-complement after:bg-white after:content-[''] after:lg:hidden":
                filterActive(),
            })}
          >
            <FilterIcon
              className={classNames('relative w-5 -rotate-90 ', {
                'bg-primary fill-primary stroke-primary-complement': onMobile,
                'bg-white fill-white stroke-stone-600': !onMobile,
              })}
            />
          </div>
          {t('MobileMenu.filter')}
        </div>

        <div
          onClick={() => setSearchOptionsType(FilterModalType.ORDER_MOBILE)}
          className={classNames(
            "mr-3 flex h-full items-center before:mr-1.5 before:inline-flex before:h-[18px] before:w-[20px] before:bg-contain before:bg-no-repeat before:content-[''] before:icon-filter-sort-mobile lg:before:bg-center",
          )}
        >
          {t('MobileMenu.sort')}
        </div>
        {onMobile && <div className="w-20"></div>}
      </div>
      <div
        className={classNames(
          'row-start-2 hidden min-w-0  leading-[42px] lg:col-span-1 lg:col-start-2 lg:col-end-3 lg:row-start-1 lg:flex',
        )}
        ref={filterContainerRef}
        onClick={(e) => {
          e.stopPropagation()
          props.closeWizard()
        }}
      >
        <DropDowns currency={props.currency} />
        <div className="flex flex-row justify-start overflow-hidden" ref={flexibleButtonAreaRef}>
          <>
            {' '}
            <SearchOption
              onClick={() => setSearchOptionsType(FilterModalType.GUESTS)}
              id="guests"
              label={
                numberOfGuestInStore === MIN_NUMBER_OF_GUESTS
                  ? t('SearchPage.buttonGuestPlural')
                  : `${numberOfGuestInStore} ${t('SearchPage.buttonGuestPlural')}`
              }
            />
            <SearchOption
              onClick={() => setSearchOptionsType(FilterModalType.PRICE)}
              id="price"
              label={t(maximumPriceInStore ? `< ${maximumPriceInStore}` : 'SearchPage.buttonPrice')}
            />
          </>
          <SearchOption
            onClick={() => setSearchOptionsType(FilterModalType.RATING)}
            id="rating"
            label={t(
              ratingInStore ? `SearchPage.Rating.${ratingInStore}` : 'SearchPage.buttonRating',
            )}
          />
          <SearchOption
            onClick={() => setSearchOptionsType(FilterModalType.LOCATION)}
            label={locationLabel(t)}
            id="location"
          />
        </div>
        <div
          className="flex grow"
          ref={staticButtonAreaRef}
          onClick={() => {
            setSearchOptionsType(undefined)
          }}
        >
          <SearchOptionButton
            id="filter"
            additionalClassName={classNames('relative flex-row px-3', {
              'bg-primary-lightened': filterActive(),
            })}
            onClick={(event: MouseEvent) => {
              setSearchOptionsType(FilterModalType.ALL)
              window.store?.dispatch(setActiveStep(undefined))
              event.stopPropagation()
            }}
          >
            {t('SearchPage.buttonFilters')}
            <svg
              viewBox="0 0 23 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="ml-2.5 mr-1 h-[20px] w-[20px]"
            >
              <g clipPath="url(#clip0_1_2)">
                <path
                  d="M16.1177 30C16.8665 30 17.5847 29.7025 18.1142 29.173C18.6437 28.6435 18.9411 27.9253 18.9411 27.1765C18.9411 26.4276 18.6437 25.7094 18.1142 25.1799C17.5847 24.6504 16.8665 24.3529 16.1177 24.3529C15.3688 24.3529 14.6506 24.6504 14.1211 25.1799C13.5916 25.7094 13.2941 26.4276 13.2941 27.1765C13.2941 27.9253 13.5916 28.6435 14.1211 29.173C14.6506 29.7025 15.3688 30 16.1177 30Z"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.2941 27.1765H2"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M25.9999 27.1765H18.9412"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.4705 18.7061C11.2193 18.7061 11.9375 18.4086 12.467 17.8791C12.9966 17.3496 13.294 16.6314 13.294 15.8826C13.294 15.1337 12.9966 14.4155 12.467 13.886C11.9375 13.3565 11.2193 13.059 10.4705 13.059C9.72165 13.059 9.00348 13.3565 8.47397 13.886C7.94445 14.4155 7.64697 15.1337 7.64697 15.8826C7.64697 16.6314 7.94445 17.3496 8.47397 17.8791C9.00348 18.4086 9.72165 18.7061 10.4705 18.7061Z"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.64707 15.8822H2"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M26 15.8822H13.2941"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.1177 7.4118C16.8665 7.4118 17.5847 7.1142 18.1142 6.5847C18.6437 6.0552 18.9411 5.337 18.9411 4.5882C18.9411 3.8394 18.6437 3.1212 18.1142 2.5917C17.5847 2.0622 16.8665 1.76475 16.1177 1.76475C15.3688 1.76475 14.6506 2.0622 14.1211 2.5917C13.5916 3.1212 13.2941 3.8394 13.2941 4.5882C13.2941 5.337 13.5916 6.0552 14.1211 6.5847C14.6506 7.1142 15.3688 7.4118 16.1177 7.4118Z"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.2941 4.5882L2 4.5882"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M25.9999 4.5882H18.9412"
                  stroke="#585858"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_1_2">
                  <rect
                    width="31.5"
                    height="27"
                    fill="white"
                    transform="translate(0.5 31.5) rotate(-90)"
                  />
                </clipPath>
              </defs>
            </svg>
            {filterActive() && (
              <DotIndicator additionalClassName="top-[-4px] right-[-4px] h-3 w-3" />
            )}
          </SearchOptionButton>
          <SearchOptionButton
            additionalClassName={
              "px-3 after:mr-1.5 after:h-[18px] after:w-[18px] after:translate-x-[8px] after:bg-contain after:bg-no-repeat after:content-[''] after:icon-filter-arrows_sort md:relative lg:after:bg-center"
            }
            onClick={(event: MouseEvent) => {
              setSearchOptionsType(FilterModalType.ORDER)
              window.store?.dispatch(setActiveStep(undefined))
              event.stopPropagation()
            }}
            id="order"
          >
            {orderLabel()}
          </SearchOptionButton>
        </div>
      </div>
    </>
  )
}
